import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import PropTypes from 'prop-types';

import { SpacerVertical, SpacerHorizontal, IconInformation } from '@klarna/bubble-ui';

import { components as clientComponents } from 'mage-client';
import { HorizontalWrapper, defaultContainerPropTypes, Alert } from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { logEvent } from '@/utils/analytics';
import { ANALYTICS_EVENTS } from './analyticsEvents';
import {
  ContainerTemplate,
  BackButton,
  CallToActionButton,
  PreAssessmentSummary,
} from '@/components';
import { withCapabilityBeforeNext } from '@/flow/common';
import {
  getProductsFromPreAssessmentDetails,
  preAssessmentDetails,
  getMerchantAwareTranslator,
  getPML,
  isLargeExposureMerchant,
  merchantRegion,
} from '@/selectors/merchant';
import { isSME } from '@/selectors/widget';
import { CAPS } from '@/constants';
import { CY_PRE_ASSESSMENT_SUBMIT_STEP_CONTAINER_IDS } from './constants';

const tPrefix = 'products-fe.containers.pre_assessment_summary';

export class PreAssessmentSubmitStep extends React.Component {
  constructor(props) {
    super(props);

    props.initFlow();
  }

  onNext = () => {
    const { onNext, mid, preAssessment, pml, isSME } = this.props;
    const { isSubmitted, showOverview, ...preAssessmentDetails } = preAssessment;

    onNext({
      scrollBeforeNext: true,
      payload: {
        params: { mid },
        body: {
          ...preAssessmentDetails,
          pml,
          isSME,
        },
      },
    })
      .then(() => this.props.initFlow())
      .catch(console.error);
  };

  buttons = () => {
    const { tMerchantAware, hasBack, onBack, isDisabled, isLoading } = this.props;
    return (
      <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
        {hasBack && (
          <>
            <BackButton
              onClick={onBack}
              disabled={isDisabled}
              {...generateCyElemId(CY_PRE_ASSESSMENT_SUBMIT_STEP_CONTAINER_IDS.BTN_BACK, window)}
            >
              {tMerchantAware(`${tPrefix}.buttons.back`)}
            </BackButton>
            <SpacerHorizontal spaceToken="space/300" />
          </>
        )}
        <CallToActionButton
          onClick={this.onNext}
          disabled={isDisabled}
          loading={isLoading}
          {...generateCyElemId(CY_PRE_ASSESSMENT_SUBMIT_STEP_CONTAINER_IDS.BTN_SUBMIT, window)}
        >
          {tMerchantAware(`${tPrefix}.buttons.submit`)}
        </CallToActionButton>
      </HorizontalWrapper>
    );
  };

  banner = () => {
    const { tMerchantAware, isLargeExposureMerchant } = this.props;

    if (!isLargeExposureMerchant) {
      return <></>;
    }

    return (
      <Alert
        icon={IconInformation}
        type="warning"
        content={tMerchantAware(`${tPrefix}.banner.mcc_deal`)}
      />
    );
  };

  render() {
    const { tMerchantAware, preAssessment, selectedProducts, goToStep, error, isSME } = this.props;
    return (
      <ContainerTemplate
        title={tMerchantAware(`${tPrefix}.title`)}
        subtitle={tMerchantAware(`${tPrefix}.subtitle`)}
        error={error}
      >
        {this.banner()}
        <PreAssessmentSummary
          preAssessment={preAssessment}
          selectedProducts={selectedProducts}
          t={tMerchantAware}
          edit={goToStep}
          isSME={isSME}
        />
        <SpacerVertical spaceToken="space/600" />
        {this.buttons()}
      </ContainerTemplate>
    );
  }
}

PreAssessmentSubmitStep.propTypes = {
  ...defaultContainerPropTypes,
  goToStep: PropTypes.func.isRequired,
  merchantRegion: PropTypes.string.isRequired,
  preAssessment: PropTypes.object.isRequired,
  selectedProducts: PropTypes.array.isRequired,
  tMerchantAware: PropTypes.func.isRequired,
  error: PropTypes.object,
};

export default compose(
  clientComponents.withPromiseBasedCapabilities({
    getUnderwritingStatus: CAPS.V2_PRE_ASSESSMENT_UNDERWRITING_STATUS_GET,
  }),
  connect(
    state => ({
      tMerchantAware: getMerchantAwareTranslator(state),
      merchantRegion: merchantRegion(state),
      preAssessment: preAssessmentDetails(state),
      selectedProducts: getProductsFromPreAssessmentDetails(state),
      pml: getPML(state),
      isLargeExposureMerchant: isLargeExposureMerchant(state),
      isSME: isSME(state),
    }),
    {},
    (stateProps, dispatchProps, ownProps) => ({
      ...stateProps,
      ...dispatchProps,
      ...ownProps,
      goToStep: path => {
        ownProps.history.push(`${ownProps.basePath}/${path}` + ownProps.history.location.search);
      },
    }),
  ),
  withCapabilityBeforeNext(
    { submit: CAPS.V2_KYM_PRE_ASSESSMENT_POST },
    ({ props: { mid, getUnderwritingStatus, merchantRegion } }) => {
      logEvent(ANALYTICS_EVENTS.preAssessmentSubmit);
      getUnderwritingStatus({ params: { mid }, region: merchantRegion });
    },
  ),
)(PreAssessmentSubmitStep);
